import { ApiError } from "api/client";
import { addEntityTreeLink, createEntity, getTreeEntities, removeEntityTreeLink, updateEntity } from "api/entities";
import { addRelation, getEntityRelations, removeRelation } from "api/relations";
import type { CustomPropertyDto, EntityDto, EntityRelationDto, LinkedTreeDto, ReferenceLinkDto } from "api/types";
import { fieldLimits } from "config";
import { useDialogFocus } from "hooks/useDialogFocus";
import React, { type ChangeEvent, type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";
import { flipRelationPerspective, formatRelationType } from "utilities";

import CustomAttrRow from "./CustomAttrRow";
import {
	byEntityTitle,
	generationalRelationSubjectGender,
	isGenerationalRelationType,
	UNRESTRICTED_OPTIONS
} from "./entityFormConfigs";
import type {
	CustomProp,
	EntityDialogProps,
	PersonFormState,
	ReferenceLinkDraft,
	RelationDirection,
	RelationDraft,
	RelationOption,
	TreeLinkDraft
} from "./entityForms.types";
import EntityImageGallery from "./EntityImageGallery";
import LineIcon from "./LineIcon";
import ReferenceLinkRow from "./ReferenceLinkRow";
import RelationChipItem from "./RelationChipItem";
import RelationTypePicker from "./RelationTypePicker";
import TreeLinkPicker from "./TreeLinkPicker";
import { useEntityImages } from "./useEntityImages";

import styles from "./formDialog.module.css";

const PERSON_TARGET = ["Person"];

const RELATION_OPTIONS: RelationOption[] = [
	{ value: "Father", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Mother", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Husband", direction: "fromTarget", targetTypes: PERSON_TARGET, canBeEx: true },
	{ value: "Wife", direction: "fromTarget", targetTypes: PERSON_TARGET, canBeEx: true },
	{ value: "Son", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Daughter", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Brother", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Sister", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Grandfather", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Grandmother", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Grandson", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Granddaughter", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Uncle", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Aunt", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Nephew", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Niece", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Cousin", direction: "fromTarget", targetTypes: PERSON_TARGET },
	{ value: "Friend", direction: "fromTarget", targetTypes: PERSON_TARGET, canBeEx: true },
	{ value: "Enemy", direction: "fromTarget", targetTypes: ["Person", "Creature", "Organization"], canBeEx: true },
	{ value: "LoveInterest", direction: "fromTarget", targetTypes: PERSON_TARGET, canBeEx: true },
	{ value: "Rival", direction: "fromTarget", targetTypes: PERSON_TARGET, canBeEx: true },
	{ value: "Acquaintance", direction: "fromTarget", targetTypes: PERSON_TARGET },
	// Succession/reincarnation, read naturally: "this person is Predecessor/Successor Of the target".
	{ value: "PredecessorOf", direction: "toTarget", targetTypes: PERSON_TARGET },
	{ value: "SuccessorOf", direction: "toTarget", targetTypes: PERSON_TARGET },
	// This person belongs to / leads / is allied with the target organization or faction.
	{ value: "MemberOf", direction: "toTarget", targetTypes: ["Organization"], canBeEx: true },
	{ value: "LeaderOf", direction: "toTarget", targetTypes: ["Organization"], canBeEx: true },
	{
		value: "AlliedWith",
		direction: "toTarget",
		targetTypes: ["Person", "Organization", "Place", "Species"],
		canBeEx: true
	},
	// This person is bonded with / accompanied by the target creature.
	{ value: "BondedWith", direction: "toTarget", targetTypes: ["Creature"], canBeEx: true },
	{ value: "HasCompanion", direction: "toTarget", targetTypes: ["Creature"], canBeEx: true },
	// This person owns the target (place, object, or creature).
	{ value: "Owner", direction: "toTarget", targetTypes: ["Place", "Object", "Creature"], canBeEx: true },
	// The target object is this person's primary weapon.
	{ value: "PrimaryWeapon", direction: "fromTarget", targetTypes: ["Object"], canBeEx: true },
	// This person guards / is guarded by / uses the target. All three take a Person on the From side,
	// but only the Organization and Custom forms offered them, so a person could never be recorded as
	// protecting anyone or using anything.
	{ value: "Protects", direction: "toTarget", targetTypes: ["Place", "Person", "Organization"], canBeEx: true },
	{ value: "ProtectedBy", direction: "toTarget", targetTypes: ["Person", "Organization"], canBeEx: true },
	{ value: "UserOf", direction: "toTarget", targetTypes: ["Object", "Custom"], canBeEx: true },
	// Read as "this person is <type> of the target" — Influences covers the mentor and teacher edges
	// that previously had no representation beyond a meaningless "Other".
	...UNRESTRICTED_OPTIONS.map((option: RelationOption) => ({ ...option, direction: "toTarget" as const })),
	{
		value: "Other",
		direction: "fromTarget",
		targetTypes: ["Person", "Place", "Object", "Creature", "Organization"],
		canBeEx: true
	}
];

const newId = (): string =>
	typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

const createInitialForm = (entity?: EntityDto | null): PersonFormState => ({
	alias: entity?.alias ?? "",
	born: entity?.born ?? "",
	description: entity?.description ?? "",
	gender: entity?.gender ?? "Male",
	genderOther: entity?.genderOther ?? "",
	name: entity?.title ?? "",
	nickname: entity?.nickname ?? "",
	specialty: entity?.specialty ?? "",
	title: entity?.personTitle ?? ""
});

const PersonFormDialog = ({ entity, treeId, onClose, onCreated }: EntityDialogProps) => {
	const { t } = useTranslation("homePage");
	const { getAccessToken } = useSession();
	const dialogRef = useDialogFocus<HTMLFormElement>();
	const isEdit = Boolean(entity);

	const [form, setForm] = useState<PersonFormState>(() => createInitialForm(entity));
	const gallery = useEntityImages({
		entityId: entity?.id ?? null,
		initialImages: entity?.images,
		getAccessToken
	});
	const [customProps, setCustomProps] = useState<CustomProp[]>(
		() =>
			entity?.customProperties?.map((prop: CustomPropertyDto) => ({
				id: prop.id,
				key: prop.key,
				value: prop.value ?? ""
			})) ?? []
	);
	const [referenceLinks, setReferenceLinks] = useState<ReferenceLinkDraft[]>(
		() => entity?.referenceLinks?.map((link: ReferenceLinkDto) => ({ id: link.id, url: link.url })) ?? []
	);
	const [treeLinks, setTreeLinks] = useState<TreeLinkDraft[]>(
		() =>
			entity?.linkedTrees?.map((link: LinkedTreeDto) => ({
				id: link.id,
				existingId: link.id,
				treeId: link.treeId,
				title: link.title
			})) ?? []
	);
	const [removedTreeLinkIds, setRemovedTreeLinkIds] = useState<string[]>([]);
	const [treeEntities, setTreeEntities] = useState<EntityDto[]>([]);
	const [relationType, setRelationType] = useState<string>("");
	const [relationTarget, setRelationTarget] = useState<string>("");
	const [relationDirection, setRelationDirection] = useState<RelationDirection>("fromTarget");
	const [relationIsEx, setRelationIsEx] = useState(false);
	const [relations, setRelations] = useState<RelationDraft[]>([]);
	const [removedRelationIds, setRemovedRelationIds] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState("");

	// Null until a type is picked. The dialog used to open on RELATION_OPTIONS[0], so leaving the
	// dropdown alone silently recorded "Father"; nothing downstream may assume a relation now.
	const selectedOption = useMemo<RelationOption | null>(
		() => RELATION_OPTIONS.find((option: RelationOption) => option.value === relationType) ?? null,
		[relationType]
	);
	const availableTargets = useMemo<EntityDto[]>(
		() =>
			selectedOption
				? treeEntities
						.filter((item: EntityDto) => selectedOption.targetTypes.includes(item.type))
						.sort(byEntityTitle)
				: [],
		[selectedOption, treeEntities]
	);
	// Direction preview for noun-labelled (fromTarget) types: "Father × Ajay" is ambiguous on its own,
	// and reading it backwards silently inverts the family hierarchy. Spell out what will be recorded.
	// Generational types get explicit direction radios instead; toTarget types (Member Of, Owner, ...)
	// already read correctly left-to-right, and "Other" carries no direction worth explaining.
	const directionHint = useMemo<string>(() => {
		if (
			!selectedOption ||
			selectedOption.direction !== "fromTarget" ||
			selectedOption.value === "Other" ||
			!relationTarget ||
			isGenerationalRelationType(selectedOption.value)
		) {
			return "";
		}

		const target = availableTargets.find((item: EntityDto) => item.id === relationTarget);

		return target
			? t("editor.personForm.relationDirectionHint", {
					target: target.title,
					type: formatRelationType(selectedOption.value)
			  })
			: "";
	}, [availableTargets, relationTarget, selectedOption, t]);

	// Family types that span generations (Father, Daughter, ...) are the ones where a flipped
	// direction silently turns the tree upside-down, so the direction is chosen explicitly here.
	// Default it from the two genders: picking "Father" with a female target while this person is
	// male almost certainly means "this person is the Father of the target", not the reverse.
	const isDirectionalType = isGenerationalRelationType(relationType);
	const directionTarget = useMemo<EntityDto | null>(
		() => availableTargets.find((item: EntityDto) => item.id === relationTarget) ?? null,
		[availableTargets, relationTarget]
	);

	useEffect(() => {
		const subjectGender = generationalRelationSubjectGender[relationType];
		const targetGender = directionTarget?.gender;

		if (subjectGender && targetGender && targetGender !== subjectGender && form.gender === subjectGender) {
			setRelationDirection("toTarget");
		} else {
			setRelationDirection("fromTarget");
		}
	}, [directionTarget, form.gender, relationType]);

	const onDirectionFromTarget = useCallback((): void => setRelationDirection("fromTarget"), []);
	const onDirectionToTarget = useCallback((): void => setRelationDirection("toTarget"), []);

	useEffect(() => {
		let active = true;

		getAccessToken()
			.then((token: string) => (token ? getTreeEntities(treeId, token) : null))
			.then((list: EntityDto[] | null) => {
				if (active && list) {
					setTreeEntities(list.filter((item: EntityDto) => item.id !== entity?.id));
				}
			})
			.catch(() => undefined);

		return () => {
			active = false;
		};
	}, [entity?.id, getAccessToken, treeId]);

	// Edit mode: show the person's existing relationships so they can be removed.
	useEffect(() => {
		if (!entity) {
			return undefined;
		}

		let active = true;

		getAccessToken()
			.then((token: string) => (token ? getEntityRelations(entity.id, token) : null))
			.then((list: EntityRelationDto[] | null) => {
				if (active && list) {
					setRelations(
						list.map((relation: EntityRelationDto) => {
							// The API returns "<related> is <type> of this person"; the chips read from this
							// person's perspective, so each row is flipped the way the other four dialogs
							// already do it. Its direction then follows from the flipped type's own option
							// entry — hardcoding "fromTarget" contradicted the table for every toTarget
							// type, so a loaded chip and a freshly added one could show the same words
							// while meaning opposite things.
							const relationType = flipRelationPerspective(relation.relationType);
							const option = RELATION_OPTIONS.find((item: RelationOption) => item.value === relationType);

							return {
								id: relation.id,
								direction: option?.direction ?? "fromTarget",
								existingId: relation.id,
								relationType,
								isEx: relation.isEx,
								targetId: relation.relatedEntityId,
								targetTitle: relation.relatedEntityTitle
							};
						})
					);
				}
			})
			.catch(() => undefined);

		return () => {
			active = false;
		};
	}, [entity, getAccessToken]);

	const updateField = useCallback(
		(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
			const { name, value } = event.target;
			setForm((current: PersonFormState) => ({ ...current, [name]: value }));
		},
		[]
	);

	const addCustomProp = useCallback((): void => {
		setCustomProps((current: CustomProp[]) => [...current, { id: newId(), key: "", value: "" }]);
	}, []);

	const updateCustomProp = useCallback((id: string, field: "key" | "value", value: string): void => {
		setCustomProps((current: CustomProp[]) =>
			current.map((prop: CustomProp) => (prop.id === id ? { ...prop, [field]: value } : prop))
		);
	}, []);

	const removeCustomProp = useCallback((id: string): void => {
		setCustomProps((current: CustomProp[]) => current.filter((prop: CustomProp) => prop.id !== id));
	}, []);

	const addReferenceLink = useCallback((): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) => [...current, { id: newId(), url: "" }]);
	}, []);

	const updateReferenceLink = useCallback((id: string, url: string): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) =>
			current.map((link: ReferenceLinkDraft) => (link.id === id ? { ...link, url } : link))
		);
	}, []);

	const removeReferenceLink = useCallback((id: string): void => {
		setReferenceLinks((current: ReferenceLinkDraft[]) =>
			current.filter((link: ReferenceLinkDraft) => link.id !== id)
		);
	}, []);

	const addTreeLink = useCallback((linkTreeId: string, title: string): void => {
		setTreeLinks((current: TreeLinkDraft[]) => [...current, { id: newId(), treeId: linkTreeId, title }]);
	}, []);

	const removeTreeLink = useCallback(
		(id: string): void => {
			const removed = treeLinks.find((link: TreeLinkDraft) => link.id === id);
			if (removed?.existingId) {
				const existingId = removed.existingId;
				setRemovedTreeLinkIds((ids: string[]) => [...ids, existingId]);
			}

			setTreeLinks((current: TreeLinkDraft[]) => current.filter((link: TreeLinkDraft) => link.id !== id));
		},
		[treeLinks]
	);

	const addRelationDraft = useCallback((): void => {
		if (!selectedOption || !relationTarget) {
			return;
		}

		const target = availableTargets.find((item: EntityDto) => item.id === relationTarget);
		if (!target) {
			return;
		}

		setRelations((current: RelationDraft[]) => [
			...current,
			{
				id: newId(),
				direction: isGenerationalRelationType(relationType) ? relationDirection : selectedOption.direction,
				relationType,
				isEx: Boolean(selectedOption.canBeEx) && relationIsEx,
				targetId: target.id,
				targetTitle: target.title
			}
		]);
		setRelationTarget("");
		setRelationIsEx(false);
	}, [availableTargets, relationDirection, relationIsEx, relationTarget, relationType, selectedOption]);

	const removeRelationDraft = useCallback(
		(id: string): void => {
			const removed = relations.find((relation: RelationDraft) => relation.id === id);
			if (removed?.existingId) {
				const existingId = removed.existingId;
				setRemovedRelationIds((ids: string[]) => [...ids, existingId]);
			}

			setRelations((current: RelationDraft[]) => current.filter((relation: RelationDraft) => relation.id !== id));
		},
		[relations]
	);

	const onRelationTypeChange = useCallback((nextType: string): void => {
		setRelationType(nextType);
		// The new type may allow a different set of targets, and may not support the ex modifier.
		setRelationTarget("");
		setRelationIsEx(false);
	}, []);

	const onRelationTargetChange = useCallback((event: ChangeEvent<HTMLSelectElement>): void => {
		setRelationTarget(event.target.value);
	}, []);

	const onRelationIsExChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setRelationIsEx(event.target.checked);
	}, []);

	const onSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();

			const name = form.name.trim();
			if (!name) {
				setError(t("editor.personForm.nameRequired"));

				return;
			}

			const token = await getAccessToken();
			if (!token) {
				return;
			}

			setError("");
			setIsSaving(true);

			try {
				const fields = {
					title: name,
					gender: form.gender,
					genderOther: form.gender === "Other" ? form.genderOther.trim() || undefined : undefined,
					alias: form.alias.trim() || undefined,
					born: form.born.trim() || undefined,
					personTitle: form.title.trim() || undefined,
					nickname: form.nickname.trim() || undefined,
					description: form.description.trim() || undefined,
					specialty: form.specialty.trim() || undefined,
					customProperties: customProps
						.filter((prop: CustomProp) => prop.key.trim())
						.map((prop: CustomProp) => ({
							key: prop.key.trim(),
							value: prop.value.trim() || undefined
						})),
					referenceLinks: referenceLinks.map((link: ReferenceLinkDraft) => link.url.trim()).filter(Boolean)
				};

				const saved = entity
					? await updateEntity(entity.id, { ...fields, images: gallery.toInputPayload() }, token)
					: await createEntity(
							treeId,
							{ type: "Person", ...fields, images: gallery.toInputPayload() },
							token
					  );

				await Promise.all(removedRelationIds.map((relationId: string) => removeRelation(relationId, token)));

				// "fromTarget" reads "<target> is <type> of this person"; "toTarget" reads "this person is <type> of <target>".
				await Promise.all(
					relations
						.filter((relation: RelationDraft) => !relation.existingId)
						.map((relation: RelationDraft) =>
							addRelation(
								{
									fromEntityId: relation.direction === "toTarget" ? saved.id : relation.targetId,
									toEntityId: relation.direction === "toTarget" ? relation.targetId : saved.id,
									relationType: relation.relationType,
									isEx: relation.isEx
								},
								token
							)
						)
				);

				await Promise.all(removedTreeLinkIds.map((linkId: string) => removeEntityTreeLink(linkId, token)));
				await Promise.all(
					treeLinks
						.filter((link: TreeLinkDraft) => !link.existingId)
						.map((link: TreeLinkDraft) => addEntityTreeLink(saved.id, link.treeId, token))
				);

				onCreated(saved.id);
				onClose();
			} catch (submitError) {
				setError(submitError instanceof ApiError ? submitError.message : t("editor.personForm.saveError"));
			} finally {
				setIsSaving(false);
			}
		},
		[
			customProps,
			entity,
			form,
			gallery,
			getAccessToken,
			onClose,
			onCreated,
			referenceLinks,
			relations,
			removedRelationIds,
			removedTreeLinkIds,
			t,
			treeId,
			treeLinks
		]
	);

	const hasTargets = availableTargets.length > 0;

	return (
		<div className={styles.personPanelOverlay}>
			<form
				ref={dialogRef}
				aria-labelledby="person-form-title"
				aria-modal="true"
				className={styles.personFormPanel}
				onSubmit={onSubmit}
				role="dialog">
				<div className={styles.personFormHeader}>
					<div className={styles.personFormHeaderCopy}>
						<h1 id="person-form-title">
							{isEdit ? t("editor.personForm.editTitle") : t("editor.personForm.addTitle")}
						</h1>
						<p>{t("editor.personForm.subtitle")}</p>
					</div>

					<button
						aria-label={t("editor.personForm.close")}
						className={styles.personFormCloseButton}
						onClick={onClose}
						type="button">
						<LineIcon name="close" />
					</button>
				</div>

				<div className={styles.personFormBody}>
					<div className={styles.personFormProfile}>
						<EntityImageGallery gallery={gallery} title={form.name} editable />
					</div>

					<div className={styles.personFormFields}>
						<h2>{t("editor.personForm.standardAttributes")}</h2>

						<div className={styles.personFormGrid}>
							<div className={styles.personFormField}>
								<label htmlFor="person-name">
									{t("editor.personForm.name")} <span className={styles.requiredStar}>*</span>
								</label>
								<input
									id="person-name"
									maxLength={fieldLimits.entityTitle}
									name="name"
									onChange={updateField}
									type="text"
									value={form.name}
									required
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="person-gender">{t("editor.personForm.gender")}</label>
								<select id="person-gender" name="gender" onChange={updateField} value={form.gender}>
									<option value="Male">{t("editor.personForm.male")}</option>
									<option value="Female">{t("editor.personForm.female")}</option>
									<option value="Other">{t("editor.personForm.other")}</option>
								</select>
							</div>

							{form.gender === "Other" && (
								<div className={`${styles.personFormField} ${styles.personFormFieldFull}`}>
									<label htmlFor="person-gender-other">{t("editor.personForm.other")}</label>
									<input
										id="person-gender-other"
										maxLength={fieldLimits.genderOther}
										name="genderOther"
										onChange={updateField}
										placeholder={t("editor.personForm.genderOtherPlaceholder")}
										type="text"
										value={form.genderOther}
									/>
								</div>
							)}

							<div className={styles.personFormField}>
								<label htmlFor="person-alias">{t("editor.personForm.alias")}</label>
								<input
									id="person-alias"
									maxLength={fieldLimits.customPropertyValue}
									name="alias"
									onChange={updateField}
									type="text"
									value={form.alias}
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="person-born">{t("editor.personForm.born")}</label>
								<input
									id="person-born"
									maxLength={fieldLimits.customPropertyValue}
									name="born"
									onChange={updateField}
									type="text"
									value={form.born}
								/>
							</div>

							<div className={`${styles.personFormField} ${styles.personFormFieldFull}`}>
								<label htmlFor="person-title">{t("editor.personForm.titleLabel")}</label>
								<input
									id="person-title"
									maxLength={fieldLimits.customPropertyValue}
									name="title"
									onChange={updateField}
									type="text"
									value={form.title}
								/>
							</div>

							<div className={styles.personFormField}>
								<label htmlFor="person-nickname">{t("editor.personForm.nickname")}</label>
								<input
									id="person-nickname"
									maxLength={fieldLimits.customPropertyValue}
									name="nickname"
									onChange={updateField}
									type="text"
									value={form.nickname}
								/>
							</div>

							<div className={`${styles.personFormField} ${styles.personFormFieldFull}`}>
								<label htmlFor="person-specialty">{t("editor.personForm.specialty")}</label>
								<input
									id="person-specialty"
									maxLength={fieldLimits.customPropertyValue}
									name="specialty"
									onChange={updateField}
									type="text"
									value={form.specialty}
								/>
							</div>

							<div className={`${styles.personFormField} ${styles.personFormFieldFull}`}>
								<label htmlFor="person-description">{t("editor.personForm.description")}</label>
								<textarea
									id="person-description"
									maxLength={fieldLimits.customPropertyValue}
									name="description"
									onChange={updateField}
									rows={4}
									value={form.description}
								/>
							</div>
						</div>
					</div>

					<section className={styles.personFormSection}>
						<h2>{t("editor.personForm.relationships")}</h2>
						<div className={styles.relationAddRow}>
							<RelationTypePicker
								controlClassName={styles.relationSelect}
								disabled={isSaving}
								onChange={onRelationTypeChange}
								options={RELATION_OPTIONS}
								value={relationType}
							/>
							<select
								aria-label={t("editor.personForm.relationTarget")}
								className={styles.relationSelect}
								disabled={!hasTargets || isSaving}
								onChange={onRelationTargetChange}
								value={relationTarget}>
								<option value="">{t("editor.personForm.relationTargetPlaceholder")}</option>
								{availableTargets.map((target: EntityDto) => (
									<option key={target.id} value={target.id}>
										{target.type === "Person" ? target.title : `${target.title} (${target.type})`}
									</option>
								))}
							</select>

							{selectedOption?.canBeEx && (
								<label className={styles.relationExToggle} htmlFor="relation-is-ex">
									<input
										checked={relationIsEx}
										disabled={isSaving}
										id="relation-is-ex"
										onChange={onRelationIsExChange}
										type="checkbox"
									/>
									<span>{t("editor.personForm.formerRelationship")}</span>
								</label>
							)}
							<button
								className={styles.relationAddButton}
								disabled={!relationType || !relationTarget || isSaving}
								onClick={addRelationDraft}
								type="button">
								<LineIcon name="plus" />
								<span>{t("editor.personForm.addRelationship")}</span>
							</button>
						</div>

						{isDirectionalType && directionTarget && (
							<div
								aria-label={t("editor.personForm.relationDirectionLabel", {
									type: formatRelationType(relationType)
								})}
								className={styles.relationDirectionGroup}
								role="radiogroup">
								<label className={styles.relationDirectionOption} htmlFor="relation-direction-from">
									<input
										checked={relationDirection === "fromTarget"}
										disabled={isSaving}
										id="relation-direction-from"
										name="relation-direction"
										onChange={onDirectionFromTarget}
										type="radio"
									/>
									<span>
										{t("editor.personForm.relationDirectionSentence", {
											subject: directionTarget.title,
											type: formatRelationType(relationType),
											object: form.name.trim() || t("editor.personForm.thisPerson")
										})}
									</span>
								</label>
								<label className={styles.relationDirectionOption} htmlFor="relation-direction-to">
									<input
										checked={relationDirection === "toTarget"}
										disabled={isSaving}
										id="relation-direction-to"
										name="relation-direction"
										onChange={onDirectionToTarget}
										type="radio"
									/>
									<span>
										{t("editor.personForm.relationDirectionSentence", {
											subject: form.name.trim() || t("editor.personForm.thisPerson"),
											type: formatRelationType(relationType),
											object: directionTarget.title
										})}
									</span>
								</label>
							</div>
						)}

						{directionHint && <p className={styles.relationHint}>{directionHint}</p>}
						{!selectedOption && (
							<p className={styles.relationHint}>{t("editor.relationPicker.chooseTypeHint")}</p>
						)}
						{selectedOption && !hasTargets && (
							<p className={styles.relationHint}>{t("editor.personForm.noPeopleHint")}</p>
						)}

						{relations.length > 0 && (
							<div className={styles.relationChips}>
								{relations.map((relation: RelationDraft) => (
									<RelationChipItem
										key={relation.id}
										onRemove={removeRelationDraft}
										relation={relation}
										removeLabel={t("editor.personForm.removeRelationship")}
									/>
								))}
							</div>
						)}
					</section>

					<TreeLinkPicker
						currentTreeId={treeId}
						links={treeLinks}
						onAdd={addTreeLink}
						onRemove={removeTreeLink}
					/>

					<section className={styles.personFormSection}>
						<h2>{t("editor.personForm.customAttributes")}</h2>
						{customProps.map((prop: CustomProp) => (
							<CustomAttrRow
								key={prop.id}
								idPrefix="person"
								keyLabel={t("editor.personForm.key")}
								onChange={updateCustomProp}
								onRemove={removeCustomProp}
								prop={prop}
								removeLabel={t("editor.personForm.removeCustomAttribute")}
								valueLabel={t("editor.personForm.value")}
							/>
						))}
						<button className={styles.customAttrAddButton} onClick={addCustomProp} type="button">
							<LineIcon name="plus" />
							<span>{t("editor.personForm.addCustomAttribute")}</span>
						</button>
					</section>

					<section className={styles.personFormSection}>
						<h2>{t("editor.personForm.references")}</h2>
						{referenceLinks.map((link: ReferenceLinkDraft) => (
							<ReferenceLinkRow
								key={link.id}
								link={link}
								onChange={updateReferenceLink}
								onRemove={removeReferenceLink}
								removeLabel={t("editor.personForm.removeReference")}
								urlLabel={t("editor.personForm.referenceUrl")}
							/>
						))}
						<button className={styles.customAttrAddButton} onClick={addReferenceLink} type="button">
							<LineIcon name="plus" />
							<span>{t("editor.personForm.addReference")}</span>
						</button>
					</section>

					{error && <div className={styles.personFormError}>{error}</div>}
				</div>

				<div className={styles.personFormActions}>
					<button
						className={styles.personFormCancelButton}
						disabled={isSaving}
						onClick={onClose}
						type="button">
						{t("editor.personForm.cancel")}
					</button>
					<button className={styles.personFormSaveButton} disabled={isSaving} type="submit">
						{isSaving ? t("editor.personForm.saving") : t("editor.personForm.save")}
					</button>
				</div>
			</form>
		</div>
	);
};

export default PersonFormDialog;
